import { Box, Stack } from "@repo/ui/core";
import {
  IconCheck,
  IconFlask,
  IconCode,
  IconClock,
} from "@repo/ui/icons";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";
import type { ComponentType } from "react";

type DeployMode = {
  label: string;
  desc: string;
  color: string;
};

const MODES: DeployMode[] = [
  {
    label: "SaaS",
    desc: "Multi-cloud, multi-tenant managed platform",
    color: "#4ade80",
  },
  {
    label: "Dedicated SaaS",
    desc: "Fully isolated network & infrastructure, we manage",
    color: "#22d3ee",
  },
  {
    label: "On-Premise",
    desc: "Deployed into customer or GitLab-managed account",
    color: "#a78bfa",
  },
];

type StatusKey = "supported" | "limited" | "testing" | "developing";

const STATUS_CONFIG: Record<
  StatusKey,
  {
    icon: ComponentType<{ size?: number; stroke?: number }>;
    color: string;
    label: string;
  }
> = {
  supported: { icon: IconCheck, color: "#4ade80", label: "Fully supported" },
  limited: { icon: IconFlask, color: "#f59e0b", label: "Limited support" },
  testing: { icon: IconClock, color: "#22d3ee", label: "Testing" },
  developing: { icon: IconCode, color: "#818cf8", label: "Developing" },
};

type Feature = {
  label: string;
  aws: StatusKey;
  gcp: StatusKey;
};

const FEATURES: Feature[] = [
  { label: "Data plane", aws: "supported", gcp: "supported" },
  { label: "Control plane", aws: "supported", gcp: "supported" },
  { label: "Workspaces", aws: "supported", gcp: "supported" },
  { label: "Nested virtualization", aws: "limited", gcp: "supported" },
  { label: "Multi-region", aws: "testing", gcp: "supported" },
  { label: "GPU time-slicing", aws: "developing", gcp: "testing" },
  { label: "Bare metal ARM", aws: "testing", gcp: "testing" },
  { label: "Bare metal x86", aws: "testing", gcp: "testing" },
];

const ROW_H = 34;
const TABLE_X = 0;
const LABEL_W = 220;
const COL_W = 180;
const TABLE_W = LABEL_W + COL_W * 2;
const SVG_W = TABLE_W;

function StatusCell({ status, x, y }: { status: StatusKey; x: number; y: number }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <g>
      <foreignObject x={x + 8} y={y + (ROW_H - 16) / 2} width={16} height={16}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={14} stroke={2} />
        </div>
      </foreignObject>
      <text
        x={x + 30}
        y={y + ROW_H / 2 + 4}
        fill={cfg.color}
        fontSize={10.5}
        fontWeight={500}
        fontFamily="inherit"
      >
        {cfg.label}
      </text>
    </g>
  );
}

export function InfraAutomationSlide() {
  const headerY = 0;
  const tableStartY = ROW_H + 4;
  const tableH = tableStartY + FEATURES.length * ROW_H + 8;
  const modesY = tableH + 24;
  const modesH = 80;
  const svgH = modesY + modesH;

  return (
    <Box w="100%" maw={1400} px="xl" h="100%">
      <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
        <SlideTitle
          title="Infrastructure Automation"
          subtitle="Managed platform — multi-cloud, multi-tenant, zero customer IaC"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          style={{ flex: 1, minHeight: 0 }}
        >
          <svg
            viewBox={`0 0 ${SVG_W} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "66vh" }}
          >
            {/* Table header */}
            <rect
              x={TABLE_X}
              y={headerY}
              width={TABLE_W}
              height={ROW_H}
              rx={8}
              fill="rgba(255,255,255,0.04)"
            />
            <text
              x={TABLE_X + 14}
              y={headerY + ROW_H / 2 + 4}
              fill="rgba(255,255,255,0.5)"
              fontSize={10}
              fontWeight={700}
              fontFamily="inherit"
              letterSpacing="0.06em"
            >
              FEATURE
            </text>
            <text
              x={TABLE_X + LABEL_W + COL_W / 2}
              y={headerY + ROW_H / 2 + 4}
              fill="#fb923c"
              fontSize={10}
              fontWeight={700}
              fontFamily="inherit"
              letterSpacing="0.06em"
              textAnchor="middle"
            >
              AWS
            </text>
            <text
              x={TABLE_X + LABEL_W + COL_W + COL_W / 2}
              y={headerY + ROW_H / 2 + 4}
              fill="#60a5fa"
              fontSize={10}
              fontWeight={700}
              fontFamily="inherit"
              letterSpacing="0.06em"
              textAnchor="middle"
            >
              GCP
            </text>

            {/* Table rows */}
            {FEATURES.map((f, i) => {
              const y = tableStartY + i * ROW_H;
              return (
                <motion.g
                  key={f.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.06, duration: 0.3 }}
                >
                  {i % 2 === 0 && (
                    <rect
                      x={TABLE_X}
                      y={y}
                      width={TABLE_W}
                      height={ROW_H}
                      fill="rgba(255,255,255,0.015)"
                    />
                  )}
                  <line
                    x1={TABLE_X}
                    y1={y}
                    x2={TABLE_X + TABLE_W}
                    y2={y}
                    stroke="rgba(255,255,255,0.04)"
                    strokeWidth={1}
                  />
                  <text
                    x={TABLE_X + 14}
                    y={y + ROW_H / 2 + 4}
                    fill="rgba(255,255,255,0.8)"
                    fontSize={11}
                    fontWeight={500}
                    fontFamily="inherit"
                  >
                    {f.label}
                  </text>
                  <StatusCell status={f.aws} x={TABLE_X + LABEL_W} y={y} />
                  <StatusCell
                    status={f.gcp}
                    x={TABLE_X + LABEL_W + COL_W}
                    y={y}
                  />
                </motion.g>
              );
            })}

            {/* Deployment modes */}
            <motion.g
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
            >
              <text
                x={TABLE_X}
                y={modesY}
                fill="rgba(255,255,255,0.4)"
                fontSize={9}
                fontWeight={700}
                fontFamily="inherit"
                letterSpacing="0.08em"
              >
                DEPLOYMENT MODELS
              </text>
              {MODES.map((m, i) => {
                const modeGap = 12;
                const modeW = (TABLE_W - modeGap * 2) / 3;
                const mx = TABLE_X + i * (modeW + modeGap);
                const my = modesY + 12;
                const cardH = modesH - 16;
                return (
                  <g key={m.label}>
                    <rect
                      x={mx}
                      y={my}
                      width={modeW}
                      height={cardH}
                      rx={8}
                      fill="rgba(255,255,255,0.025)"
                      stroke={m.color}
                      strokeWidth={1}
                      strokeOpacity={0.3}
                    />
                    <circle
                      cx={mx + 14}
                      cy={my + 16}
                      r={4}
                      fill={m.color}
                    />
                    <text
                      x={mx + 26}
                      y={my + 19}
                      fill={m.color}
                      fontSize={11}
                      fontWeight={700}
                      fontFamily="inherit"
                    >
                      {m.label}
                    </text>
                    <foreignObject
                      x={mx + 12}
                      y={my + 28}
                      width={modeW - 24}
                      height={cardH - 34}
                    >
                      <div
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: 9,
                          fontFamily: "inherit",
                          lineHeight: 1.4,
                          overflow: "hidden",
                        }}
                      >
                        {m.desc}
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
